import { ref, computed } from 'vue';
import CertificationService, {
  CertificationConfigResponse,
  CertificationStatusResponse
} from '@/core/services/CertificationService';
import { getCertificationRouteName } from '@/core/helpers/certification-routes';

export interface CertificationStep {
  id?: number;
  order: number;
  type: string;
  component: string;
  deadline_days?: number | null;
  required: boolean;
  title: string;
  message: string | null;
  description: string;
  attachments?: Array<{
    id: number;
    name: string;
    mime_type: string | null;
    file_size: number | null;
  }>;
}

/**
 * Composable para gerenciar steps de certificação
 * 
 * Carrega e gerencia a configuração e status da certificação, incluindo:
 * - Lista de steps e suas configurações (incluindo deadline_days)
 * - Step atual do usuário
 * - Navegação entre steps
 * - Validação de acesso aos steps
 * 
 * @param certificationId - ID da certificação
 * @param currentComponent - Componente atual para validação de step correto
 */
export function useCertificationSteps(certificationId: string, currentComponent?: string) {
  const loading = ref(false);
  const strategyType = ref<string>('');
  const currentStep = ref<number>(0);
  const steps = ref<CertificationStep[]>([]);
  const statusData = ref<any>(null);
  const configData = ref<any>(null);

  /**
   * Encontra o step esperado para um componente específico
   */
  const expectedStepForComponent = computed(() => {
    if (!currentComponent || steps.value.length === 0) return null;
    
    const stepConfig = steps.value.find(step => step.component === currentComponent);
    return stepConfig ? stepConfig.order : null;
  });

  /**
   * Verifica se o usuário está no step correto para este componente
   */
  const isOnCorrectStep = computed(() => {
    if (!expectedStepForComponent.value) return false;
    return currentStep.value === expectedStepForComponent.value;
  });

  /**
   * Obtém o step atual completo
   */
  const currentStepData = computed((): CertificationStep | null => {
    if (currentStep.value === 0 || steps.value.length === 0) return null;
    return steps.value.find(step => step.order === currentStep.value) || null;
  });

  /**
   * Obtém o deadline_days do step atual
   */
  const currentStepDeadlineDays = computed((): number | null | undefined => {
    return currentStepData.value?.deadline_days;
  });

  /**
   * Obtém o próximo step baseado no step atual
   */
  const getNextStep = (): CertificationStep | null => {
    if (steps.value.length === 0) return null;
    
    const nextStepConfig = steps.value.find(step => step.order === currentStep.value + 1);
    return nextStepConfig || null;
  };

  /**
   * Obtém o step anterior baseado no step atual
   */
  const getPreviousStep = (): CertificationStep | null => {
    if (steps.value.length === 0) return null;
    
    const prevStepConfig = steps.value.find(step => step.order === currentStep.value - 1);
    return prevStepConfig || null;
  };

  /**
   * Obtém o nome da rota do próximo step
   */
  const getNextStepRoute = (): string | null => {
    const nextStep = getNextStep();
    if (!nextStep) return null;
    
    return getCertificationRouteName(nextStep.component);
  };

  /**
   * Obtém o nome da rota do step anterior
   */
  const getPreviousStepRoute = (): string | null => {
    const prevStep = getPreviousStep();
    if (!prevStep) return null;
    
    return getCertificationRouteName(prevStep.component);
  };

  /**
   * Obtém o step pelo order
   */
  const getStepByOrder = (order: number): CertificationStep | null => {
    return steps.value.find(step => step.order === order) || null;
  };

  /**
   * Obtém o step pelo tipo (ex: 'modeling', 'execution').
   */
  const getStepByType = (type: string): CertificationStep | null => {
    return steps.value.find(step => step.type === type) || null;
  };

  /**
   * Obtém a rota de um step específico
   */
  const getStepRoute = (stepOrder: number): string | null => {
    const step = getStepByOrder(stepOrder);
    if (!step) return null;
    
    return getCertificationRouteName(step.component);
  };

  /**
   * Carrega a configuração e status da certificação
   */
  const loadCertificationData = async (): Promise<void> => {
    loading.value = true;
    
    try {
      // Busca config e status em paralelo
      const [configResponse, statusResponse] = await Promise.all([
        CertificationService.getConfig(certificationId),
        CertificationService.getStatus(certificationId)
      ]);

      if (configResponse.status === 200) {
        configData.value = configResponse.data.data;
        strategyType.value = configResponse.data.data.strategy_type;
        steps.value = configResponse.data.data.steps || [];
      }

      if (statusResponse.status === 200) {
        statusData.value = statusResponse.data.data;
        currentStep.value = statusResponse.data.data.current_step;
      }
    } catch (error) {
      console.error('Erro ao carregar dados da certificação:', error);
      throw error;
    } finally {
      loading.value = false;
    }
  };

  return {
    // States
    loading,
    strategyType,
    currentStep,
    steps,
    statusData,
    configData,
    
    // Computed
    expectedStepForComponent,
    isOnCorrectStep,
    currentStepData,
    currentStepDeadlineDays,
    
    // Methods
    loadCertificationData,
    getNextStep,
    getPreviousStep,
    getNextStepRoute,
    getPreviousStepRoute,
    getStepByOrder,
    getStepByType,
    getStepRoute
  };
}
